import React, { useEffect, useMemo, useState } from 'react';
import { moduleClientFactory } from '../api-service/module-client-factory';

export const RoleThemeTypes = {
  Authority: 'Authority',
  ServiceProvider: 'ServiceProvider',
  MedicalCollaborator: 'MedicalCollaborator',
} as const;

interface RoleThemesContext {
  isAuthority: boolean;
  isServiceProvider: boolean;
  isMedicalCollaborator: boolean;
  isOnlyServiceProvider: boolean;
  isOnlyMedicalCollaborator: boolean;
  isOnlyServiceProviderOrMedicalCollaborator: boolean;
  rolesLoaded: boolean;
}

const RoleThemesContext = React.createContext<RoleThemesContext>({
  isAuthority: false,
  isServiceProvider: false,
  isMedicalCollaborator: false,
  isOnlyServiceProvider: false,
  isOnlyMedicalCollaborator: false,
  isOnlyServiceProviderOrMedicalCollaborator: false,
  rolesLoaded: false,
});

type RoleThemesProviderProps = {
  children: React.ReactNode;
};

const authorizationClient = moduleClientFactory.createAuthorizationClient();

export function RoleThemesProvider({ children }: RoleThemesProviderProps) {
  const [roleThemes, setRoleThemes] = useState<string[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchRoleThemes() {
      try {
        const response = await authorizationClient.getRoleThemes();
        if (!cancelled) {
          setRoleThemes(response.roleThemes ?? []);
          setRolesLoaded(true);
        }
      } catch {
        // silently ignore - defaults to no roles
        if (!cancelled) setRolesLoaded(true);
      }
    }

    fetchRoleThemes();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<RoleThemesContext>(
    () => ({
      isAuthority: roleThemes.includes(RoleThemeTypes.Authority),
      isServiceProvider: roleThemes.includes(RoleThemeTypes.ServiceProvider),
      isMedicalCollaborator: roleThemes.includes(RoleThemeTypes.MedicalCollaborator),
      isOnlyServiceProvider: roleThemes.length > 0 && roleThemes.every(t => t === RoleThemeTypes.ServiceProvider),
      isOnlyMedicalCollaborator: roleThemes.length > 0 && roleThemes.every(t => t === RoleThemeTypes.MedicalCollaborator),
      isOnlyServiceProviderOrMedicalCollaborator:
        roleThemes.length > 0 && roleThemes.every(t => t === RoleThemeTypes.ServiceProvider || t === RoleThemeTypes.MedicalCollaborator),
      rolesLoaded,
    }),
    [roleThemes, rolesLoaded]
  );

  return <RoleThemesContext.Provider value={value}>{children}</RoleThemesContext.Provider>;
}

export const useRoleThemes = (): RoleThemesContext => React.useContext(RoleThemesContext);
